import { useState } from 'react';
import styled from 'styled-components';
import { Typography, Popover } from '@mui/material';
import { ReactComponent as DeleteIcon } from '@elementary/common-ui/assets/16x16/delete-trash.svg';
import {
  Flex,
  Button,
  OnSubmitEventVal,
  IconButton,
  AddableList,
  ListItem,
} from '@elementary/common-ui/components';
import { FilterOption } from '@elementary/common-ui/components/FilterSelector';
import FilterChipRow from '@elementary/common-ui/components/Table/TableFilters/FilterChipRow';
import FilterSelector from '@elementary/common-ui/components/FilterSelector';
import { PermissionsAccess, ResourceFilterGroup } from '../../shared/types';
import PermissionsAccessDropdownOrigin from './PermissionsAccessDropdown';

const PermissionsAccessDropdown = styled(PermissionsAccessDropdownOrigin)`
  align-self: flex-start;
`;

const FilterGroupItem = styled(ListItem)`
  gap: 12px;
  flex-direction: column;
  align-items: stretch;
  position: relative;

  &:hover .delete-icon {
    opacity: 1;
  }
`;

const FilterGroupContent = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

const EmptyFilterGroup = styled(Flex)`
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const NoFiltersText = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray4};
`;

const DeleteIconButton = styled(IconButton).attrs({
  size: 'small',
  className: 'delete-icon',
})`
  opacity: 0;
  transition: opacity 0.2s;
  display: flex;
  height: 32px;

  &.delete-icon {
    opacity: 0;
  }

  svg {
    display: block;
  }
`;

const SectionWrapper = styled(Flex)`
  width: 100%;
  justify-content: space-between;
  gap: 16px;
`;

const PopoverStyled = styled(Popover)`
  .MuiPaper-root {
    min-width: 700px;
    padding: 24px;
    box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 6px;
    border: 1px solid ${({ theme }) => theme.palette.common.gray8};
  }
`;

type Props = {
  filterGroups: ResourceFilterGroup[];
  filterOptions: FilterOption[];
  onAddFilterGroup: () => void;
  onAddFilterToGroup: (groupIndex: number, val: OnSubmitEventVal) => void;
  onUpdateFilterInGroup: (groupIndex: number, val: OnSubmitEventVal) => void;
  onRemoveFilterFromGroup: (groupIndex: number, filterIndex: number) => void;
  onChangeGroupAccess: (groupIndex: number, access: PermissionsAccess) => void;
  onRemoveFilterGroup: (groupIndex: number) => void;
};

const ResourceFilterGroups = ({
  filterGroups,
  filterOptions,
  onAddFilterGroup,
  onAddFilterToGroup,
  onUpdateFilterInGroup,
  onRemoveFilterFromGroup,
  onChangeGroupAccess,
  onRemoveFilterGroup,
}: Props) => {
  const [currentGroupIndex, setCurrentGroupIndex] = useState<number | null>(
    null,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const onClose = () => {
    setAnchorEl(null);
    setCurrentGroupIndex(null);
  };

  return (
    <>
      <AddableList onAdd={onAddFilterGroup} addButtonText="+ Add filter group">
        {filterGroups.map((group, groupIndex) => {
          const onDeleteClick = () => onRemoveFilterGroup(groupIndex);

          return (
            <FilterGroupItem key={groupIndex}>
              <FilterGroupContent>
                {group.filters.length === 0 ? (
                  <SectionWrapper>
                    <EmptyFilterGroup>
                      <NoFiltersText>No filters selected</NoFiltersText>
                      <Button
                        color="secondary"
                        onClick={(e) => {
                          setCurrentGroupIndex(groupIndex);
                          setAnchorEl(e.currentTarget);
                        }}
                      >
                        Add filter
                      </Button>
                    </EmptyFilterGroup>
                    <DeleteIconButton onClick={onDeleteClick}>
                      <DeleteIcon />
                    </DeleteIconButton>
                    <PermissionsAccessDropdown
                      value={group.access}
                      onChange={(access) =>
                        onChangeGroupAccess(groupIndex, access)
                      }
                    />
                  </SectionWrapper>
                ) : (
                  <SectionWrapper>
                    <FilterChipRow
                      filterOptions={filterOptions}
                      selectedFilters={group.filters.map((filter) => ({
                        property: filter.property,
                        operator: filter.operator,
                        value: filter.value,
                      }))}
                      onSubmit={(val) => onUpdateFilterInGroup(groupIndex, val)}
                      onAddFilter={(val) => {
                        onAddFilterToGroup(groupIndex, val);
                      }}
                      clearFilter={(property, operator) => {
                        const filterIndex = group.filters.findIndex(
                          (f) =>
                            f.property === property && f.operator === operator,
                        );
                        if (filterIndex !== -1) {
                          onRemoveFilterFromGroup(groupIndex, filterIndex);
                        }
                      }}
                    />
                    <Flex sx={{ gap: 2 }}>
                      <DeleteIconButton onClick={onDeleteClick}>
                        <DeleteIcon />
                      </DeleteIconButton>
                      <PermissionsAccessDropdown
                        value={group.access}
                        onChange={(access) =>
                          onChangeGroupAccess(groupIndex, access)
                        }
                      />
                    </Flex>
                  </SectionWrapper>
                )}
              </FilterGroupContent>
            </FilterGroupItem>
          );
        })}
      </AddableList>

      <PopoverStyled
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={onClose}
        anchorOrigin={{
          vertical: 50,
          horizontal: 'left',
        }}
      >
        <Typography sx={{ fontWeight: 'bold', mb: 3 }} variant="body1">
          Add filter
        </Typography>

        <FilterSelector
          filterOptions={filterOptions}
          onSubmit={(val) => {
            if (currentGroupIndex !== null) {
              onAddFilterToGroup(currentGroupIndex, val);
            }
            onClose();
          }}
          onClose={() => {
            onClose();
          }}
          multiselect
        />
      </PopoverStyled>
    </>
  );
};

export default ResourceFilterGroups;
